import { lazy, Suspense, useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Bell } from 'lucide-react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { NavigationSidebar } from './components/layout/NavigationSidebar';
import type { RecentActivity } from './components/layout/NavigationSidebar';
import { NotificationsPanel } from './features/notifications/NotificationsPanel';
import { LoginPage } from './pages/LoginPage';
import { SetupPage } from './pages/SetupPage';
import { api, authApi, ApiError, type Submission, type User } from './services/api';
import { log } from './utils/logger';
import { useAppStore, hasActiveSession } from './stores/appStore';

// ── Lazy page imports ─────────────────────────────────────────────────────────
// Each page is only downloaded when the user navigates to it.
const Dashboard        = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const ChecklistWizard  = lazy(() => import('./pages/ChecklistWizard').then(m => ({ default: m.ChecklistWizard })));
const ChecklistView    = lazy(() => import('./pages/ChecklistView').then(m => ({ default: m.ChecklistView })));
const DocumentUpload   = lazy(() => import('./features/upload/DocumentUpload').then(m => ({ default: m.DocumentUpload })));
const ValidationResults = lazy(() => import('./pages/ValidationResults').then(m => ({ default: m.ValidationResults })));
const AuditTrail       = lazy(() => import('./pages/AuditTrail').then(m => ({ default: m.AuditTrail })));
const AllChecklists    = lazy(() => import('./pages/AllChecklists').then(m => ({ default: m.AllChecklists })));
const Documents        = lazy(() => import('./pages/Documents').then(m => ({ default: m.Documents })));
const GlobalAuditTrail = lazy(() => import('./pages/GlobalAuditTrail').then(m => ({ default: m.GlobalAuditTrail })));
const AdminUsersPage   = lazy(() => import('./pages/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const ProfilePage      = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));

// ── Shell context — shared between AppShell and its route wrappers ────────────
interface ShellCtxValue {
  user: User;
  logActivity: (message: string, checklistId?: string) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  fetchNotifications: () => void;
}
const ShellCtx = createContext<ShellCtxValue | null>(null);
const useShellCtx = () => useContext(ShellCtx)!;

// ── Suspense fallback shown while a lazy page chunk is loading ────────────────
function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading page" className="flex items-center justify-center flex-1 h-full">
      <div
        className="w-8 h-8 rounded-full border-2 animate-spin"
        style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}
      />
    </div>
  );
}

// ── Route wrapper components ──────────────────────────────────────────────────
// These are thin adapters: they read URL params + context and pass typed
// props to the page components, keeping the pages themselves router-agnostic.

function DashboardRoute() {
  const navigate = useNavigate();
  const { logActivity, user } = useShellCtx();
  return (
    <Dashboard
      onViewDetails={(id) => navigate(`/checklist/${id}`)}
      onValidate={(id) => navigate(`/validation/${id}`)}
      onChecklistCreated={(submission: Submission) => {
        navigate(`/checklist/${submission.id}`);
        logActivity(
          `Generated ${submission.name || submission.submission_type || 'new'} checklist`,
          submission.id,
        );
      }}
      currentUser={user}
    />
  );
}

function WizardRoute() {
  const navigate = useNavigate();
  const { logActivity, fetchNotifications } = useShellCtx();

  const handleComplete = async (submissionType: string, deadline: string) => {
    try {
      const submission = await api.createSubmission(submissionType, deadline);
      navigate(`/checklist/${submission.id}`);
      logActivity(
        `Generated ${submission.name || submission.submission_type || submissionType} checklist`,
        submission.id,
      );
      fetchNotifications();
    } catch (error) {
      log.error('Failed to create submission:', error);
      alert('Failed to create submission. Please try again.');
    }
  };

  return <ChecklistWizard onComplete={handleComplete} onCancel={() => navigate('/dashboard')} />;
}

function ChecklistViewRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logActivity } = useShellCtx();
  return (
    <ChecklistView
      checklistId={id!}
      onUploadDocuments={() => navigate(`/upload/${id}`)}
      onValidate={() => navigate(`/validation/${id}`)}
      onViewAudit={() => navigate(`/audit/${id}`)}
      onBack={() => navigate('/dashboard')}
      onActivityLog={logActivity}
    />
  );
}

function UploadRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logActivity, fetchNotifications } = useShellCtx();
  return (
    <DocumentUpload
      checklistId={id!}
      onComplete={(checklistId) => {
        navigate(`/checklist/${checklistId}`);
        fetchNotifications();
      }}
      onCancel={() => navigate(`/checklist/${id}`)}
      onActivityLog={logActivity}
    />
  );
}

function ValidationResultsRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logActivity, setSidebarCollapsed } = useShellCtx();
  return (
    <ValidationResults
      checklistId={id!}
      onBack={() => navigate(`/checklist/${id}`)}
      onViewAudit={() => navigate(`/audit/${id}`)}
      onActivityLog={logActivity}
      onSidebarCollapseChange={setSidebarCollapsed}
    />
  );
}

function AuditTrailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logActivity } = useShellCtx();
  return (
    <AuditTrail
      checklistId={id!}
      onBack={() => navigate(`/checklist/${id}`)}
      onActivityLog={logActivity}
    />
  );
}

function AllChecklistsRoute() {
  const navigate = useNavigate();
  return (
    <AllChecklists
      onViewDetails={(id) => navigate(`/checklist/${id}`)}
      onValidate={(id) => navigate(`/validation/${id}`)}
      onBack={() => navigate('/dashboard')}
    />
  );
}

function DocumentsRoute() {
  const navigate = useNavigate();
  return <Documents onBack={() => navigate('/dashboard')} />;
}

function GlobalAuditRoute() {
  const navigate = useNavigate();
  return <GlobalAuditTrail onNavigateToChecklist={(id) => navigate(`/checklist/${id}`)} />;
}

function AdminUsersRoute() {
  const { user } = useShellCtx();
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <AdminUsersPage currentUser={user} />;
}

function ProfileRoute() {
  const { user } = useShellCtx();
  return <ProfilePage userName={user.name} userEmail={user.email} userRole={user.role} />;
}

// ── Helpers for NavigationSidebar ─────────────────────────────────────────────
function locationToPageId(pathname: string): string {
  if (pathname.startsWith('/checklist'))     return 'checklist';
  if (pathname.startsWith('/upload'))        return 'upload';
  if (pathname.startsWith('/validation'))    return 'validation';
  if (pathname.startsWith('/audit'))         return 'audit';
  if (pathname.startsWith('/wizard'))        return 'wizard';
  if (pathname.startsWith('/all-checklists')) return 'all_checklists';
  if (pathname.startsWith('/documents'))     return 'documents';
  if (pathname.startsWith('/global-audit'))  return 'global_audit';
  if (pathname.startsWith('/admin'))         return 'admin_users';
  if (pathname.startsWith('/profile'))       return 'profile';
  return 'dashboard';
}

function pageIdToPath(page: string, checklistId?: string): string {
  switch (page) {
    case 'all_checklists': return '/all-checklists';
    case 'global_audit':   return '/global-audit';
    case 'admin_users':    return '/admin/users';
    case 'checklist':      return checklistId ? `/checklist/${checklistId}` : '/dashboard';
    default:               return `/${page}`;
  }
}

const PAGE_TITLES: Record<string, string> = {
  dashboard:      'Dashboard',
  wizard:         'Generate New Checklist',
  checklist:      'Checklist View',
  upload:         'Document Upload',
  validation:     'Validation Results',
  audit:          'Audit Trail',
  all_checklists: 'All Checklists',
  documents:      'Documents',
  global_audit:   'Audit Trail',
  admin_users:    'User Management',
  profile:        'My Profile',
};

// ── App — auth gate ───────────────────────────────────────────────────────────
export default function App() {
  const user  = useAppStore((s) => s.user);
  const login = useAppStore((s) => s.login);
  const setUser = useAppStore((s) => s.setUser);
  const logout  = useAppStore((s) => s.logout);

  const [setupChecked, setSetupChecked] = useState(false);
  const [needsSetup,   setNeedsSetup]   = useState(false);
  const [authChecked,  setAuthChecked]  = useState(false);

  useEffect(() => {
    authApi.setupStatus()
      .then(({ needs_setup }) => { setNeedsSetup(needs_setup); setSetupChecked(true); })
      .catch(() => setSetupChecked(true));
  }, []);

  // Always fetch the canonical role from the server — never trust sessionStorage.
  useEffect(() => {
    if (hasActiveSession()) {
      authApi.me()
        .then((u) => { setUser(u); setAuthChecked(true); })
        .catch(() => { setUser(null); setAuthChecked(true); });
    } else {
      setAuthChecked(true);
    }
  }, [setUser]);

  if (!setupChecked || !authChecked) return null;
  if (needsSetup)    return <SetupPage onSetupComplete={() => setNeedsSetup(false)} />;
  if (!user)         return <LoginPage onLogin={login} />;

  return <AppShell user={user} onLogout={logout} />;
}

// ── AppShell — main layout with routing ──────────────────────────────────────
function AppShell({ user, onLogout }: { user: User; onLogout: () => void | Promise<void> }) {
  const setNotifications = useAppStore((s) => s.setNotifications);
  const showToast        = useAppStore((s) => s.showToast);
  const toastNotification = useAppStore((s) => s.toastNotification);
  const presentToast     = useAppStore((s) => s.presentToast);
  const dismissToast     = useAppStore((s) => s.dismissToast);

  const navigate = useNavigate();
  const location = useLocation();

  const currentPageId = locationToPageId(location.pathname);
  const pageTitle     = PAGE_TITLES[currentPageId] ?? 'Dashboard';

  const ACTIVITY_KEY = `recent_activity_${user.id}`;
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>(() => {
    try {
      const stored = localStorage.getItem(ACTIVITY_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored) as Array<RecentActivity & { timestamp: string }>;
      return parsed.map((a) => ({ ...a, timestamp: new Date(a.timestamp) }));
    } catch {
      return [];
    }
  });
  const [sidebarCollapsed,    setSidebarCollapsed]    = useState(false);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);

  const seenNotificationIds = useRef<Set<string>>(
    new Set(JSON.parse(localStorage.getItem('seenNotificationIds') ?? '[]') as string[]),
  );
  const logActivityRef = useRef<(message: string, checklistId?: string) => void>(() => {});

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await api.getNotifications() as Array<Record<string, unknown>>;
      const unread = data.filter((n) => !n['is_read']);
      const newUnread = unread.filter((n) => !seenNotificationIds.current.has(n['id'] as string));

      if (newUnread.length > 0) {
        const first = newUnread[0]!;
        presentToast({
          id:          first['id'] as string,
          title:       'New Notification',
          description: first['message'] as string,
          type:        first['type'] as string | undefined,
          checklistId: first['submission_id'] as string | undefined,
          action:      first['action'] as string | undefined,
        });
        setTimeout(() => dismissToast(), 5000);

        const hasValidation = newUnread.some((n) => n['action'] === 'validation_results');
        newUnread
          .filter((n) => n['action'] === 'validation_results' || (n['action'] === 'auto_map' && !hasValidation))
          .forEach((n) => logActivityRef.current(n['message'] as string, n['submission_id'] as string | undefined));
      }

      data.forEach((n) => seenNotificationIds.current.add(n['id'] as string));
      localStorage.setItem('seenNotificationIds', JSON.stringify([...seenNotificationIds.current]));

      setNotifications(data as Parameters<typeof setNotifications>[0]);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) { void onLogout(); return; }
      log.error('Failed to fetch notifications:', error);
    }
  }, [onLogout, setNotifications, presentToast, dismissToast]);

  useEffect(() => {
    void fetchNotifications();
    const interval = setInterval(() => { void fetchNotifications(); }, 5000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify(recentActivities));
  }, [recentActivities, ACTIVITY_KEY]);

  const logActivity = useCallback((message: string, checklistId?: string) => {
    const entry: RecentActivity = {
      id: `activity-${Date.now()}`,
      message,
      timestamp: new Date(),
      checklistId,
    };
    setRecentActivities((prev) => [entry, ...prev].slice(0, 5));
  }, []);
  logActivityRef.current = logActivity;

  const handleNotificationClick = useCallback((
    checklistId?: string,
    notificationId?: string,
    action?: string,
  ) => {
    dismissToast();
    setNotificationsPanelOpen(false);
    if (checklistId) {
      navigate(action === 'validation_results' ? `/validation/${checklistId}` : `/checklist/${checklistId}`);
    }
    if (notificationId) {
      api.markNotificationRead(notificationId)
        .then(() => fetchNotifications())
        .catch((error) => log.error('Failed to mark notification as read:', error));
    }
  }, [dismissToast, navigate, fetchNotifications]);

  const handleMarkAllRead = useCallback(() => {
    api.markAllNotificationsRead()
      .then(() => fetchNotifications())
      .catch((error) => log.error('Failed to mark all notifications as read:', error));
  }, [fetchNotifications]);

  // Stable callbacks so React.memo on Header/NavigationSidebar can bail out
  // between the 5 s notification polls when nothing relevant changed.
  const handleToggleNotifications = useCallback(
    () => setNotificationsPanelOpen((o) => !o),
    [],
  );
  const handleLogoClick    = useCallback(() => navigate('/dashboard'), [navigate]);
  const handleLogout       = useCallback(() => { void onLogout(); }, [onLogout]);
  const handleViewProfile  = useCallback(() => navigate('/profile'),   [navigate]);
  const handleSidebarNavigate = useCallback(
    (page: string, checklistId?: string) => navigate(pageIdToPath(page, checklistId)),
    [navigate],
  );

  const ctxValue: ShellCtxValue = { user, logActivity, setSidebarCollapsed, fetchNotifications };

  return (
    <ShellCtx.Provider value={ctxValue}>
      <div className="h-screen w-full flex flex-col overflow-hidden">
        <Header
          pageTitle={pageTitle}
          onNotificationClick={handleToggleNotifications}
          onLogoClick={handleLogoClick}
          onLogout={handleLogout}
          onViewProfile={handleViewProfile}
        />

        <div className="flex-1 overflow-hidden flex">
          <NavigationSidebar
            currentPage={currentPageId}
            selectedChecklistId={null}
            onNavigate={handleSidebarNavigate}
            recentActivities={recentActivities}
            isCollapsed={sidebarCollapsed}
            userRole={user.role}
          />

          <div className="flex-1 overflow-hidden">
            <Suspense fallback={<PageSkeleton />}>
              <Routes>
                <Route path="/"               element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard"      element={<DashboardRoute />} />
                <Route path="/wizard"         element={<WizardRoute />} />
                <Route path="/checklist/:id"  element={<ChecklistViewRoute />} />
                <Route path="/upload/:id"     element={<UploadRoute />} />
                <Route path="/validation/:id" element={<ValidationResultsRoute />} />
                <Route path="/audit/:id"      element={<AuditTrailRoute />} />
                <Route path="/all-checklists" element={<AllChecklistsRoute />} />
                <Route path="/documents"      element={<DocumentsRoute />} />
                <Route path="/global-audit"   element={<GlobalAuditRoute />} />
                <Route path="/admin/users"    element={<AdminUsersRoute />} />
                <Route path="/profile"        element={<ProfileRoute />} />
                <Route path="*"               element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </div>
        </div>

        <NotificationsPanel
          isOpen={notificationsPanelOpen}
          onClose={() => setNotificationsPanelOpen(false)}
          onNotificationClick={handleNotificationClick}
          onMarkAllRead={handleMarkAllRead}
        />

        {/* Toast notification */}
        {showToast && toastNotification && (
          <div
            onClick={() => handleNotificationClick(
              toastNotification.checklistId,
              toastNotification.id,
              toastNotification.action,
            )}
            className="fixed top-20 right-4 bg-white border-l-4 border-primary rounded-lg shadow-2xl p-4 min-w-[320px] max-w-md z-50 animate-slide-in cursor-pointer hover:shadow-3xl transition-shadow"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground mb-1">{toastNotification.title}</p>
                <p className="text-sm text-muted-foreground break-words">{toastNotification.description}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); dismissToast(); }}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </ShellCtx.Provider>
  );
}
